import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ritornoTelegram, dentroTelegram } from '../../services/telegramAccesso';
import ApriNelBrowser from './ApriNelBrowser';
import i18next from 'i18next';

/**
 * /login: apre la finestra «Accedi» (FinestraAccesso), non la pagina vecchia.
 *
 * Ci si arriva da un link diretto o da una pagina riservata (RequireAuth manda
 * qui con state.from). La finestra sta nel guscio, sopra la pagina: qui la si
 * richiede e la pagina dietro diventa la home. Dopo l'accesso la finestra
 * porta dove si voleva andare (state.dopoAccesso).
 *
 * La pagina vecchia (Login.js) resta nel repo ma non e' piu' collegata: va
 * tolta quando la finestra e' provata.
 */
export default function AccessoDaLink({ userService }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [attesa, setAttesa] = useState('');
  // ACCESSO AL SITO DA OPERATORE: il codice non e' valso (scaduto, gia' usato, altro sito)
  const [erroreOperatore, setErroreOperatore] = useState('');
  // il pulsante del bot ha aperto il sito DENTRO Telegram: prima si propone il browser vero
  const [fuori] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    return !!q.get('biglietto') && !q.get('qui') && dentroTelegram();
  });

  useEffect(() => {
    if (fuori) return;
    // IL RITORNO DA TELEGRAM: /login?code=... (redirect) oppure /login?biglietto=...
    // (il pulsante del bot): si entra da qui e si va alla home (o dove si voleva
    // andare), senza altri clic. Senza conto, la registrazione gia' compilata.
    const q = new URLSearchParams(window.location.search);

    // DAL BACKOFFICE: /login?operatore=<codice>. Un operatore entra sul sito
    // con il conto di un giocatore (4 novembre 2026). Il codice vale un minuto
    // e una volta sola: se non vale, lo si dice QUI, senza aprire la finestra
    // di accesso, perche' l'operatore non deve entrare come se stesso.
    const codiceOperatore = q.get('operatore');
    if (codiceOperatore) {
      setAttesa(i18next.t('Un attimo: sto entrando sul sito come operatore…'));
      const entra = typeof userService.doLoginOperatore === 'function'
        ? userService.doLoginOperatore(codiceOperatore)
        : Promise.resolve({ esito: 'errore', messaggio: '' });
      entra.then((esito) => {
        if (esito && esito.esito === 'dentro') { navigate('/', { replace: true }); return; }
        setErroreOperatore((esito && esito.messaggio) || i18next.t('Accesso al sito non riuscito'));
        setAttesa('');
      }).catch(() => {
        setErroreOperatore(i18next.t('Accesso al sito non riuscito'));
        setAttesa('');
      });
      return;
    }

    if (q.get('code') || q.get('error') || q.get('biglietto')) {
      setAttesa(i18next.t('Un attimo: sto entrando con Telegram…'));
      ritornoTelegram().then(async (r) => {
        if (!r) { navigate('/', { replace: true }); return; }
        const dove = r.dopo || '/';
        if (r.errore) {
          navigate('/', { replace: true });
          if (r.errore !== 'annullato' && typeof userService.richiediAccesso === 'function') {
            setTimeout(() => userService.richiediAccesso(), 80);
          }
          return;
        }
        if (r.scopo === 'accesso' && typeof userService.doLoginTelegram === 'function') {
          const esito = await userService.doLoginTelegram(r.idToken, r.nonce);
          if (esito && esito.esito === 'senzaConto') {
            // NESSUN CONTO con questo Telegram: la registrazione, gia' compilata, con l'avviso
            navigate('/', { replace: true });
            setTimeout(() => {
              if (typeof userService.richiediRegistrazione === 'function') userService.richiediRegistrazione('', { idToken: r.idToken, nonce: r.nonce, user: r.user }, true);
            }, 80);
            return;
          }
          if (esito && esito.esito === 'errore') {
            // il motivo (in attesa, sospeso...) si legge nella finestra di accesso
            navigate('/', { replace: true });
            setTimeout(() => { if (typeof userService.richiediAccesso === 'function') userService.richiediAccesso(); }, 80);
            return;
          }
        }
        navigate(dove, { replace: true });
      }).catch(() => navigate('/', { replace: true }));
      return;
    }

    const from = location.state && location.state.from;
    const dopoAccesso = from && from.pathname && from.pathname !== '/login'
      ? { pathname: from.pathname, search: from.search || '', hash: from.hash || '' }
      : undefined;

    navigate('/', { replace: true, state: dopoAccesso ? { dopoAccesso } : undefined });

    // La finestra si ascolta nel guscio, che al primo caricamento monta DOPO
    // di noi (gli effetti dei figli girano prima di quelli dei padri): si
    // aspetta che abbia finito, o la richiesta andrebbe persa.
    //
    // NIENTE clearTimeout all'uscita: navigate('/') qui sopra smonta questo
    // componente subito, e il cleanup cancellava la richiesta - la finestra non
    // si apriva mai da un link diretto (visto nel browser il 13/10/2026).
    setTimeout(() => {
      if (userService && typeof userService.richiediAccesso === 'function') userService.richiediAccesso();
    }, 80);
    // una volta sola, all'arrivo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (fuori) return <ApriNelBrowser />;
  if (erroreOperatore) {
    return (
      <div style={{ padding: '48px 16px', textAlign: 'center', color: 'var(--lvc-text-2)', maxWidth: 560, margin: '0 auto' }}>
        <h2 style={{ color: 'var(--lvc-text)', fontSize: 18, margin: '0 0 8px' }}>{i18next.t('Non sono riuscito a entrare come operatore')}</h2>
        <p style={{ margin: '0 0 12px' }}>{erroreOperatore}</p>
        <p style={{ margin: 0, fontSize: 13 }}>{i18next.t('Chiudi questa scheda, torna nel backoffice e premi di nuovo «Accesso al sito»: il codice vale un minuto e una volta sola.')}</p>
      </div>
    );
  }
  return attesa ? (
    <div style={{ padding: '48px 16px', textAlign: 'center', color: 'var(--lvc-text-2)' }}>{attesa}</div>
  ) : null;
}
